import React, { useCallback, useRef, useState } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import MasterLayout from "../masterLayout/MasterLayout";
import Breadcrumb from "../components/Breadcrumb";
import { organizationsService } from "../services/api";

const OrganizationsBulkUploadPage = () => {
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null); // { rows, totals }
  const [summary, setSummary] = useState(null); // { summary, errors, createdIds }
  const [publish, setPublish] = useState(true); // Vitrinde yayınla (randu_listed=1)

  const handleDownloadTemplate = useCallback(async () => {
    const res = await organizationsService.downloadBulkTemplate();
    if (!res.success) {
      toast.error(res.error || "Şablon indirilemedi");
      return;
    }
    toast.success("Örnek CSV indirildi");
  }, []);

  const handleSelectFile = (e) => {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    if (!/\.(csv|txt)$/i.test(f.name)) {
      toast.error("Lütfen .csv uzantılı bir dosya seçin");
      return;
    }
    if (f.size > 2 * 1024 * 1024) {
      toast.error("Dosya 2MB'ı geçemez");
      return;
    }
    setFile(f);
    setPreview(null);
    setSummary(null);
  };

  const handlePreview = useCallback(async () => {
    if (!file) {
      toast.error("Önce bir CSV dosyası seçin");
      return;
    }
    setPreviewing(true);
    setSummary(null);
    const res = await organizationsService.previewBulkUpload(file);
    setPreviewing(false);
    if (!res.success) {
      toast.error(res.error || res.message || "Önizleme oluşturulamadı");
      setPreview(null);
      return;
    }
    setPreview(res);
    if (res.totals && res.totals.total === 0) {
      toast.warning("CSV'de satır bulunamadı");
    } else {
      const parts = [`${res.totals.valid} geçerli`];
      if (res.totals.duplicate) parts.push(`${res.totals.duplicate} mükerrer`);
      if (res.totals.invalid) parts.push(`${res.totals.invalid} hatalı`);
      toast.info(`Toplam ${res.totals.total} satır: ${parts.join(', ')}`);
    }
  }, [file]);

  const handleUpload = useCallback(async () => {
    if (!file) {
      toast.error("Önce bir CSV dosyası seçin");
      return;
    }
    if (!preview || preview.totals.valid === 0) {
      toast.error("Yüklenebilir geçerli satır yok. Önce önizleme yapın ve hataları giderin");
      return;
    }
    setUploading(true);
    const res = await organizationsService.bulkUploadOrganizations(file, { publish });
    setUploading(false);
    if (!res.success) {
      toast.error(res.error || res.message || "Toplu yükleme başarısız");
      return;
    }
    setSummary(res);
    const s = res.summary;
    const parts = [`${s.inserted} eklendi`];
    if (s.skipped) parts.push(`${s.skipped} mükerrer atlandı`);
    if (s.failed) parts.push(`${s.failed} hata`);
    toast.success(`Yükleme tamamlandı: ${parts.join(', ')}`);
    setFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
    setPreview(null);
  }, [file, preview]);

  const totals = preview && preview.totals ? preview.totals : { total: 0, valid: 0, invalid: 0, duplicate: 0 };

  return (
    <MasterLayout>
      <Breadcrumb title="Toplu Organizasyon Yükle" />

      <div className="card mb-24">
        <div className="card-body p-24">
          <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
            <div>
              <h5 className="mb-1">CSV ile Toplu Yükleme</h5>
              <p className="text-secondary-light mb-0">
                Organizasyonları CSV dosyası ile topluca ekleyin. Önce örnek şablonu indirip doldurun, ardından
                önizleme yapın ve yükleyin.
              </p>
            </div>
            <button
              type="button"
              className="btn btn-outline-primary d-inline-flex align-items-center gap-2"
              onClick={handleDownloadTemplate}
            >
              <Icon icon="solar:download-outline" className="text-lg" />
              Örnek CSV İndir
            </button>
          </div>

          <div className="alert alert-info mt-3 mb-0">
            <div className="fw-semibold mb-1">CSV kolon açıklamaları</div>
            <ul className="mb-0 ps-3" style={{ fontSize: 13 }}>
              <li><b>name</b>: Organizasyon adı (zorunlu)</li>
              <li><b>city</b>: İl adı (zorunlu, DB ile birebir eşleşmeli – ör. "İstanbul")</li>
              <li><b>district</b>: İlçe adı (zorunlu, ilgili ile ait olmalı)</li>
              <li><b>categories</b>: Kategori adları; birden fazla için pipe kullanın: <code>Kuaför|Güzellik Salonu</code></li>
              <li><b>phone</b>: Telefon numarası (zorunlu)</li>
              <li><b>latitude</b>, <b>longitude</b>: Vitrin haritası için enlem/boylam (opsiyonel, DECIMAL)</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="card mb-24">
        <div className="card-body p-24">
          <label className="form-label fw-semibold">CSV dosyası seçin</label>
          <div className="d-flex flex-wrap align-items-center gap-3">
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,text/csv"
              className="form-control"
              style={{ maxWidth: 420 }}
              onChange={handleSelectFile}
            />
            <button
              type="button"
              className="btn btn-secondary d-inline-flex align-items-center gap-2"
              onClick={handlePreview}
              disabled={!file || previewing}
            >
              {previewing ? (
                <>
                  <span className="spinner-border spinner-border-sm" /> Önizleniyor...
                </>
              ) : (
                <>
                  <Icon icon="solar:eye-outline" /> Önizle
                </>
              )}
            </button>
            <button
              type="button"
              className="btn btn-primary d-inline-flex align-items-center gap-2"
              onClick={handleUpload}
              disabled={!preview || totals.valid === 0 || uploading}
            >
              {uploading ? (
                <>
                  <span className="spinner-border spinner-border-sm" /> Yükleniyor...
                </>
              ) : (
                <>
                  <Icon icon="solar:upload-outline" /> Yükle ({totals.valid})
                </>
              )}
            </button>
          </div>
          {file && (
            <div className="text-secondary-light mt-2" style={{ fontSize: 13 }}>
              Seçilen dosya: <b>{file.name}</b> ({Math.ceil(file.size / 1024)} KB)
            </div>
          )}
          <div className="form-check form-switch mt-3">
            <input
              id="bulk-publish-switch"
              className="form-check-input"
              type="checkbox"
              role="switch"
              checked={publish}
              onChange={(e) => setPublish(e.target.checked)}
            />
            <label htmlFor="bulk-publish-switch" className="form-check-label">
              Yüklenen organizasyonları vitrinde yayınla
              {" "}
              <span className="text-secondary-light" style={{ fontSize: 12 }}>
                (randu_listed=1 — public siteyi www.randu.app üzerinde listelensin)
              </span>
            </label>
          </div>
        </div>
      </div>

      {preview && (
        <div className="card mb-24">
          <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center flex-wrap gap-3">
            <h6 className="mb-0">Önizleme</h6>
            <span className="badge bg-primary-100 text-primary-600">Toplam: {totals.total}</span>
            <span className="badge bg-success-100 text-success-600">Geçerli: {totals.valid}</span>
            <span className="badge bg-warning-100 text-warning-600">Mükerrer: {totals.duplicate || 0}</span>
            <span className="badge bg-danger-100 text-danger-600">Hatalı: {totals.invalid}</span>
          </div>
          <div className="card-body p-24">
            <div className="table-responsive scroll-sm">
              <table className="table bordered-table sm-table mb-0">
                <thead>
                  <tr>
                    <th>Satır</th>
                    <th>Durum</th>
                    <th>Ad</th>
                    <th>İl / İlçe</th>
                    <th>Kategori</th>
                    <th>Telefon</th>
                    <th>Lat / Lng</th>
                    <th>Mesaj</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.rows.map((r) => {
                    const rowClass = r.duplicate ? "table-warning" : r.valid ? "" : "table-danger";
                    const statusBadge = r.duplicate ? (
                      <span className="badge bg-warning">Mükerrer</span>
                    ) : r.valid ? (
                      <span className="badge bg-success">Geçerli</span>
                    ) : (
                      <span className="badge bg-danger">Hatalı</span>
                    );
                    const dupMsg = r.duplicate
                      ? r.duplicate.source === 'csv'
                        ? `CSV içi mükerrer (satır ${r.duplicate.refRow} ile aynı${r.duplicate.match === 'phone' ? ' telefon' : ' ad/il/ilçe'})`
                        : `DB'de mevcut: #${r.duplicate.orgId} ${r.duplicate.orgName}${r.duplicate.match === 'phone' ? ' (aynı telefon)' : ''}`
                      : null;
                    const matchedCats = r.data.matchedCategories || [];
                    return (
                      <tr key={r.rowIndex} className={rowClass}>
                        <td>{r.rowIndex}</td>
                        <td>{statusBadge}</td>
                        <td>{r.data.name || "-"}</td>
                        <td>
                          {r.data.city || "-"}{r.data.city_id ? "" : " (?)"}
                          {" / "}
                          {r.data.district || "-"}{r.data.district_id ? "" : " (?)"}
                        </td>
                        <td>
                          {matchedCats.length === 0 ? (
                            <span className="text-secondary-light">-</span>
                          ) : (
                            <div className="d-flex flex-wrap gap-1">
                              {matchedCats.map((c, i) => (
                                <span key={i} className="badge bg-primary-100 text-primary-600">{c}</span>
                              ))}
                            </div>
                          )}
                        </td>
                        <td>{r.data.phone || "-"}</td>
                        <td>
                          {r.data.latitude !== null || r.data.longitude !== null
                            ? `${r.data.latitude ?? "-"}, ${r.data.longitude ?? "-"}`
                            : "-"}
                        </td>
                        <td style={{ fontSize: 12 }}>
                          {dupMsg && <div className="text-warning fw-semibold">{dupMsg}</div>}
                          {r.errors.length > 0 && (
                            <div className="text-danger">{r.errors.join(" | ")}</div>
                          )}
                          {r.warnings.length > 0 && (
                            <div className="text-warning-dark" style={{ opacity: 0.85 }}>
                              {r.warnings.join(" | ")}
                            </div>
                          )}
                          {!dupMsg && r.errors.length === 0 && r.warnings.length === 0 && (
                            <span className="text-success">OK</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {summary && (
        <div className="card mb-24">
          <div className="card-header border-bottom bg-base py-16 px-24">
            <h6 className="mb-0">Yükleme Sonucu</h6>
          </div>
          <div className="card-body p-24">
            <div className="d-flex flex-wrap gap-3 mb-3">
              <span className="badge bg-primary">Toplam: {summary.summary.total}</span>
              <span className="badge bg-success">Eklenen: {summary.summary.inserted}</span>
              <span className="badge bg-warning">Mükerrer (atlandı): {summary.summary.skipped || 0}</span>
              <span className="badge bg-danger">Başarısız: {summary.summary.failed}</span>
            </div>
            {Array.isArray(summary.errors) && summary.errors.length > 0 && (
              <>
                <h6 className="mt-3 mb-2">Hatalı satırlar</h6>
                <div className="table-responsive scroll-sm">
                  <table className="table bordered-table sm-table mb-0">
                    <thead>
                      <tr>
                        <th>Satır</th>
                        <th>Ad</th>
                        <th>Hata</th>
                      </tr>
                    </thead>
                    <tbody>
                      {summary.errors.map((e, i) => (
                        <tr key={i}>
                          <td>{e.row}</td>
                          <td>{e.name}</td>
                          <td className="text-danger">{e.message}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
            {Array.isArray(summary.skipped) && summary.skipped.length > 0 && (
              <>
                <h6 className="mt-3 mb-2">Atlanan mükerrer satırlar</h6>
                <div className="table-responsive scroll-sm">
                  <table className="table bordered-table sm-table mb-0">
                    <thead>
                      <tr>
                        <th>Satır</th>
                        <th>Ad</th>
                        <th>Kaynak</th>
                        <th>Eşleşme</th>
                      </tr>
                    </thead>
                    <tbody>
                      {summary.skipped.map((e, i) => (
                        <tr key={i}>
                          <td>{e.row}</td>
                          <td>{e.name}</td>
                          <td>
                            <span className={`badge ${e.source === 'csv' ? 'bg-info' : 'bg-secondary'}`}>
                              {e.source === 'csv' ? 'CSV içi' : 'DB mevcut'}
                            </span>
                          </td>
                          <td className="text-warning">{e.ref}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </MasterLayout>
  );
};

export default OrganizationsBulkUploadPage;
